import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api, { FEATURED_CITIES, TRADE_CATEGORIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Wrench, Zap, Home as HomeIcon, Hammer, Paintbrush, Thermometer, Droplets, Trees,
  Boxes, DoorOpen, PaintBucket, LayoutGrid, Truck, Sparkles, Cpu, ArrowRight, BadgeCheck,
  Star, Wallet, ShieldCheck,
} from "lucide-react";

const CATEGORY_ICONS = {
  "Handyman / General Repair": Wrench,
  "Plumbing": Droplets,
  "Electrical": Zap,
  "Roofing": HomeIcon,
  "HVAC / Heating & Cooling": Thermometer,
  "Carpentry & Woodwork": Hammer,
  "Painting (Interior / Exterior)": Paintbrush,
  "Flooring": LayoutGrid,
  "Door & Window Repair": DoorOpen,
  "Drywall & Plastering": PaintBucket,
  "Landscaping & Outdoor": Trees,
  "Moving & Hauling": Truck,
  "Cleaning & Junk Removal": Sparkles,
  "Home Renovation (General)": Boxes,
  "Smart Home / Tech Installation": Cpu,
};

export default function HomePage() {
  const nav = useNavigate();
  const [stats, setStats] = useState({ jobs_posted: 0, tradespeople: 0, provinces_covered: 0 });

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <Helmet>
        <title>Fixus — Canada's Trusted Home Services Marketplace</title>
        <meta name="description" content="Find verified plumbers, electricians, painters and tradespeople across Canada. Post your job, compare quotes, hire with confidence. Free for homeowners." />
        <meta property="og:title" content="Fixus — Canada's Home Services Marketplace" />
        <meta property="og:description" content="Find verified Canadian tradespeople. Post a job in 2 minutes and get free quotes." />
      </Helmet>
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-50 tf-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs text-slate-600 font-medium tracking-wide mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Canada's trusted home services marketplace
            </div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight leading-[1.05]">
              Find the right trade.
              <br />
              <span className="text-orange-600">Get the job done.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Post your project, compare quotes from verified Canadian tradespeople,
              and hire with confidence — from Halifax to Victoria.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid="hero-post-job"
                onClick={() => nav("/post-job")}
                className="bg-orange-600 hover:bg-orange-700 text-white h-12 px-6 text-base"
              >
                Post a Job <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                data-testid="hero-find-work"
                onClick={() => nav("/jobs")}
                variant="outline"
                className="border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white h-12 px-6 text-base"
              >
                Find Work
              </Button>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <Stat label="Jobs Posted" value={stats.jobs_posted} testId="stat-jobs" />
              <Stat label="Tradespeople" value={stats.tradespeople} testId="stat-tradespeople" />
              <Stat label="Provinces" value={stats.provinces_covered} testId="stat-provinces" />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
              <img
                src="https://images.pexels.com/photos/17063686/pexels-photo-17063686.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                alt="Tradesperson at work"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-4 bg-white rounded-xl shadow-lg border border-slate-200 p-4 flex items-center gap-3 max-w-xs">
              <div className="w-10 h-10 rounded-full bg-blue-900 text-white grid place-items-center">
                <BadgeCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">Verified pros</div>
                <div className="text-xs text-slate-500">Licensed, insured & reviewed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST & SAFETY STRIP */}
      <section
        data-testid="trust-strip"
        className="border-y border-slate-200 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-700">
            <li
              data-testid="trust-verified-pros"
              className="inline-flex items-center gap-2"
            >
              <BadgeCheck className="w-4 h-4 text-blue-900" />
              <span className="font-medium text-slate-900">
                {stats.tradespeople > 0 ? `${stats.tradespeople}+ verified pros` : "Verified pros"}
              </span>
            </li>
            <li className="hidden sm:block w-px h-4 bg-slate-200" aria-hidden />
            <li
              data-testid="trust-real-reviews"
              className="inline-flex items-center gap-2"
            >
              <Star className="w-4 h-4 text-orange-600" />
              <span>Reviewed by real homeowners</span>
            </li>
            <li className="hidden sm:block w-px h-4 bg-slate-200" aria-hidden />
            <li
              data-testid="trust-free-to-post"
              className="inline-flex items-center gap-2"
            >
              <Wallet className="w-4 h-4 text-emerald-600" />
              <span>Free to post</span>
            </li>
            <li className="hidden sm:block w-px h-4 bg-slate-200" aria-hidden />
            <li
              data-testid="trust-no-hidden-fees"
              className="inline-flex items-center gap-2"
            >
              <ShieldCheck className="w-4 h-4 text-blue-900" />
              <span>No hidden fees</span>
            </li>
          </ul>
        </div>
      </section>

      {/* BROWSE BY TRADE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">
              Browse by trade
            </div>
            <h2 className="font-display font-bold text-3xl text-slate-900">
              Whatever you need fixed, built, or installed
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 tf-stagger">
          {TRADE_CATEGORIES.map((cat) => {
            const Icon = CATEGORY_ICONS[cat] || Wrench;
            return (
              <Link
                key={cat}
                to={`/tradespeople?category=${encodeURIComponent(cat)}`}
                data-testid={`category-${cat}`}
                className="group p-5 rounded-xl bg-white border border-slate-200 tf-card-hover"
              >
                <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-blue-900 group-hover:text-white grid place-items-center transition-colors">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-medium text-slate-900 text-sm mt-3 leading-snug">
                  {cat}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* BROWSE BY CITY */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">
                Browse by city
              </div>
              <h2 className="font-display font-bold text-3xl text-slate-900">
                Local pros in cities across Canada
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {FEATURED_CITIES.map((c) => (
              <Link
                key={c.name}
                to={`/tradespeople?province=${encodeURIComponent(c.province)}&city=${encodeURIComponent(c.name)}`}
                data-testid={`city-${c.name}`}
                className="relative group rounded-xl overflow-hidden aspect-[4/5] block"
              >
                <img
                  src={c.img}
                  alt={c.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="font-display font-semibold text-lg">{c.name}</div>
                  <div className="text-xs text-slate-200 opacity-90">{c.province}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl p-8 bg-blue-900 text-white">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-300 font-semibold mb-2">
              Homeowners
            </div>
            <h3 className="font-display font-bold text-2xl mb-3">Got a project on your list?</h3>
            <p className="text-blue-100 mb-6">
              Post a job in two minutes. Compare quotes. Hire someone you trust.
            </p>
            <Button
              data-testid="cta-post-job"
              onClick={() => nav("/post-job")}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              Post a Job
            </Button>
          </div>
          <div className="rounded-2xl p-8 bg-slate-50 border border-slate-200">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">
              Tradespeople
            </div>
            <h3 className="font-display font-bold text-2xl mb-3 text-slate-900">
              Grow your trade business
            </h3>
            <p className="text-slate-600 mb-6">
              Browse local jobs in your trade. Send quotes. Get hired by real homeowners.
            </p>
            <Button
              data-testid="cta-find-work"
              onClick={() => nav("/register?role=tradesperson")}
              variant="outline"
              className="border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white"
            >
              Join as a Pro
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <div className="font-display font-bold text-3xl text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}
